/**
 * LiveDemoPage — Iter-102 · Item 8.
 *
 * Business-type demo form upgrade. Each business type selection loads a
 * dedicated, professional, detailed form representing what that business
 * type's demo account will look like on first login. NO Twilio approval
 * note appears anywhere on this page (per CEO directive).
 *
 * On submit, POST /api/live-demo/launch generates a fully pre-populated
 * demo sandbox with all submitted values plugged in — the demo account
 * looks complete from first login, not like an empty shell.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Rocket, ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { apiFetch } from '../lib/apiFetch';
import { DEMO_AMENITIES } from '../lib/demoAmenities';
import { PropertyTypePicker } from '../components/PropertyTypePicker';
import '../styles/property-intake.css';
import '../styles/public-page-titles.css';

// -----------------------------------------------------------------
// Business type catalog. Keep in sync with backend PROPERTY_TYPES.
// -----------------------------------------------------------------
// -----------------------------------------------------------------
// Field library (used by TYPE_FIELDS below).
//   type: text | number | email | tel | select | textarea | address
// -----------------------------------------------------------------
const F = {
  property_name:        { key: 'property_name',        label: 'Property name',                   type: 'text',   required: true },
  business_name:        { key: 'business_name',        label: 'Business name',                   type: 'text',   required: true },
  practice_name:        { key: 'practice_name',        label: 'Practice name',                   type: 'text',   required: true },
  restaurant_name:      { key: 'restaurant_name',      label: 'Restaurant name',                 type: 'text',   required: true },
  facility_name:        { key: 'facility_name',        label: 'Facility name',                   type: 'text',   required: true },
  institution_name:     { key: 'institution_name',     label: 'Institution name',                type: 'text',   required: true },
  center_name:          { key: 'center_name',          label: 'Center name',                     type: 'text',   required: true },
  mall_name:            { key: 'mall_name',            label: 'Mall name',                       type: 'text',   required: true },
  church_name:          { key: 'church_name',          label: 'Church / organization name',      type: 'text',   required: true },
  address:              { key: 'address',              label: 'Property address',                type: 'address',required: true },
  contact_email:        { key: 'contact_email',        label: 'Contact email',                   type: 'email',  required: true },
  contact_phone:        { key: 'contact_phone',        label: 'Contact phone',                   type: 'tel',    required: true },
  notes:                { key: 'notes',                label: 'Notes',                           type: 'textarea' },
};

// Yes/No select helper.
const YN = (key, label) => ({ key, label, type: 'select', options: ['Yes', 'No'] });

// -----------------------------------------------------------------
// TYPE_FIELDS — comprehensive field set per business type (Iter-102 · Item 8).
// -----------------------------------------------------------------
const TYPE_FIELDS = {
  residential: [
    F.property_name,
    { key: 'unit_count',           label: 'Number of units',            type: 'number', required: true },
    { key: 'building_count',       label: 'Number of buildings',        type: 'number', required: true },
    F.address,
    { key: 'management_company',   label: 'Management company name',    type: 'text' },
    { key: 'property_manager_name',label: 'Property manager name',      type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'service_schedule',     label: 'Service schedule preference',type: 'select', options: ['Daily', 'Weekly', 'Twice a Week', 'Bi-Weekly'] },
    { key: 'valet_station_count',  label: 'Number of valet trash stations', type: 'number' },
    { key: 'gate_code',            label: 'Gate code (optional)',       type: 'text' },
    F.notes,
  ],
  office: [
    F.business_name,
    { key: 'floor_count',          label: 'Number of floors',           type: 'number', required: true },
    { key: 'square_footage',       label: 'Total square footage',       type: 'number', required: true },
    F.address,
    { key: 'facility_manager_name',label: 'Facility manager name',      type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'employee_count',       label: 'Number of employees on site',type: 'number' },
    { key: 'cleaning_frequency',   label: 'Cleaning frequency preference', type: 'select', options: ['Nightly', 'Weekly', 'Twice Weekly', 'Bi-Weekly'] },
    YN('after_hours_access',       'After-hours access required'),
    YN('security_checkin',         'Security check-in required'),
    F.notes,
  ],
  retail: [
    F.center_name,
    { key: 'tenant_count',         label: 'Number of tenants / stores', type: 'number', required: true },
    F.address,
    { key: 'property_manager_name',label: 'Property manager name',      type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'operating_hours',      label: 'Operating hours',            type: 'text' },
    { key: 'high_traffic_zones',   label: 'High-traffic zones',         type: 'textarea' },
    YN('parking_lot_included',     'Parking lot included'),
    { key: 'restroom_count',       label: 'Restroom count',             type: 'number' },
    { key: 'cleaning_frequency',   label: 'Cleaning frequency preference', type: 'select', options: ['Daily', 'Weekly', 'Bi-Weekly'] },
    F.notes,
  ],
  gas_station: [
    F.business_name,
    { key: 'pump_count',           label: 'Number of fueling positions',type: 'number', required: true },
    F.address,
    { key: 'owner_manager_name',   label: 'Owner / manager name',       type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'operating_hours',      label: 'Operating hours',            type: 'select', options: ['24 hours', 'Scheduled'] },
    { key: 'restroom_count',       label: 'Restroom count',             type: 'number' },
    YN('interior_cleaning',        'Interior cleaning required'),
    YN('canopy_cleaning',          'Canopy cleaning required'),
    F.notes,
  ],
  school: [
    F.institution_name,
    { key: 'campus_type',          label: 'Campus type',                type: 'select', options: ['K-12', 'College', 'University', 'Trade School'] },
    { key: 'building_count',       label: 'Number of buildings',        type: 'number', required: true },
    F.address,
    { key: 'facilities_director_name', label: 'Facilities director name', type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'enrollment_count',     label: 'Student enrollment count',   type: 'number' },
    { key: 'operating_schedule',   label: 'Operating schedule',         type: 'select', options: ['Semester', 'Year-round'] },
    YN('after_hours_cleaning',     'After-hours cleaning required'),
    F.notes,
  ],
  medical: [
    F.practice_name,
    { key: 'specialty_type',       label: 'Specialty type',             type: 'text', required: true },
    { key: 'exam_room_count',      label: 'Number of exam rooms',       type: 'number', required: true },
    F.address,
    { key: 'office_manager_name',  label: 'Office manager name',        type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'operating_hours',      label: 'Operating hours',            type: 'text' },
    YN('biohazard_disposal',       'Biohazard disposal required'),
    { key: 'disinfection_protocol',label: 'Disinfection protocol level',type: 'select', options: ['Standard', 'Enhanced'] },
    { key: 'patient_area_count',   label: 'Patient-facing areas count', type: 'number' },
    F.notes,
  ],
  warehouse: [
    F.facility_name,
    { key: 'facility_type',        label: 'Facility type',              type: 'select', options: ['Warehouse', 'Manufacturing', 'Distribution'] },
    { key: 'square_footage',       label: 'Square footage',             type: 'number', required: true },
    F.address,
    { key: 'operations_manager_name', label: 'Operations manager name', type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'shift_schedule',       label: 'Shift schedule',             type: 'select', options: ['Day', 'Night', '24hr'] },
    YN('heavy_equipment',          'Heavy equipment present'),
    { key: 'floor_type',           label: 'Floor type',                 type: 'select', options: ['Concrete', 'Epoxy', 'Other'] },
    YN('hazardous_materials',      'Hazardous materials on site'),
    F.notes,
  ],
  restaurant: [
    F.restaurant_name,
    { key: 'cuisine_type',         label: 'Cuisine type',               type: 'text', required: true },
    { key: 'seating_capacity',     label: 'Seating capacity',           type: 'number', required: true },
    F.address,
    { key: 'owner_manager_name',   label: 'Owner / manager name',       type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'operating_hours',      label: 'Operating hours',            type: 'text' },
    YN('kitchen_deep_clean',       'Kitchen deep clean required'),
    { key: 'dining_frequency',     label: 'Dining area cleaning frequency', type: 'select', options: ['Daily', 'Weekly', 'Bi-Weekly'] },
    YN('hood_cleaning',            'Hood cleaning required'),
    { key: 'last_inspection_date', label: 'Last health inspection date (optional)', type: 'text' },
    F.notes,
  ],
  mall: [
    F.mall_name,
    { key: 'anchor_stores',        label: 'Number of anchor stores',    type: 'number', required: true },
    { key: 'tenant_count',         label: 'Total tenant count',         type: 'number', required: true },
    F.address,
    { key: 'general_manager_name', label: 'General manager name',       type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'operating_hours',      label: 'Operating hours',            type: 'text' },
    YN('food_court_present',       'Food court present'),
    { key: 'restroom_count',       label: 'Restroom count',             type: 'number' },
    YN('parking_structure',        'Parking structure included'),
    { key: 'cleaning_frequency',   label: 'Cleaning frequency preference', type: 'select', options: ['Daily', 'Nightly', 'Weekly'] },
    F.notes,
  ],
  church: [
    F.church_name,
    { key: 'denomination',         label: 'Denomination (optional)',    type: 'text' },
    { key: 'seating_capacity',     label: 'Seating capacity',           type: 'number', required: true },
    F.address,
    { key: 'facilities_coordinator_name', label: 'Facilities coordinator name', type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'service_schedule',     label: 'Service schedule (days / times)', type: 'textarea' },
    YN('fellowship_hall',          'Fellowship hall present'),
    YN('kitchen_present',          'Kitchen present'),
    YN('parking_lot_included',     'Parking lot included'),
    { key: 'cleaning_frequency',   label: 'Cleaning frequency preference', type: 'select', options: ['Weekly', 'Twice Weekly', 'Event-Ready'] },
    F.notes,
  ],
  // Iter-150 · Red Team Fix 1 + Fix 5 — Strategic Enterprise fields.
  bank: [
    F.institution_name,
    { key: 'branch_count',         label: 'Number of branches',          type: 'number', required: true },
    { key: 'square_footage',       label: 'Total square footage',        type: 'number', required: true },
    F.address,
    { key: 'facility_manager_name',label: 'Facility manager name',       type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'teller_count',         label: 'Teller counters',             type: 'number' },
    { key: 'atm_vestibule_count',  label: 'ATM vestibules',              type: 'number' },
    { key: 'drive_through_lanes',  label: 'Drive-through lanes',         type: 'number' },
    // Fix 5 — vault / secure-area handling input.
    { key: 'vault_handling',       label: 'Vault / secure-area handling', type: 'select',
      options: [
        'None — vault serviced by client staff',
        'Perimeter cleaning only (no entry)',
        'Escorted access — client-authorized procedure',
        'Standard restricted area (no NECS access)',
      ], required: true },
    { key: 'secure_area_count',    label: 'Secure / restricted areas (count)', type: 'number' },
    { key: 'cleaning_frequency',   label: 'Cleaning frequency preference', type: 'select', options: ['Nightly', 'Nightly + Day Porter', 'Bi-Weekly'] },
    YN('after_hours_access',       'After-hours access allowed'),
    YN('security_checkin',         'Security check-in required'),
    F.notes,
  ],
  stadium: [
    F.facility_name,
    { key: 'seating_capacity',     label: 'Seating capacity',            type: 'number', required: true },
    { key: 'square_footage',       label: 'Total square footage',        type: 'number', required: true },
    F.address,
    { key: 'facility_manager_name',label: 'Venue operations manager',    type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'events_per_month',     label: 'Events per month',            type: 'number', required: true },
    { key: 'event_duration_hours', label: 'Average event duration (hours)', type: 'number', required: true },
    { key: 'turnaround_window_hours', label: 'Turnaround window (hours)', type: 'number', required: true },
    { key: 'restroom_set_count',   label: 'Restroom sets',               type: 'number' },
    { key: 'concession_stand_count', label: 'Concession stands',         type: 'number' },
    { key: 'premium_area_count',   label: 'Premium clubs / suites',      type: 'number' },
    { key: 'waste_volume_tier',    label: 'Waste volume tier',           type: 'select', options: ['Light', 'Medium', 'Heavy', 'Very Heavy'] },
    YN('locker_area_access',       'Locker/team-area access authorized'),
    F.notes,
  ],
  airport: [
    F.facility_name,
    { key: 'gate_count',           label: 'Number of gates',             type: 'number', required: true },
    { key: 'square_footage',       label: 'Total square footage',        type: 'number', required: true },
    F.address,
    { key: 'facility_manager_name',label: 'Facility operations manager', type: 'text' },
    F.contact_email, F.contact_phone,
    { key: 'passenger_traffic_daily', label: 'Daily passenger traffic',  type: 'number', required: true },
    { key: 'operating_hours',      label: 'Operating hours',             type: 'select', options: ['24/7', '18h/day', '16h/day', 'Custom'] },
    { key: 'concourse_count',      label: 'Number of concourses',        type: 'number' },
    { key: 'restroom_set_count',   label: 'Restroom sets',               type: 'number' },
    { key: 'baggage_area_count',   label: 'Baggage claim areas',         type: 'number' },
    { key: 'tsa_security_zones',   label: 'TSA / security-adjacent zones', type: 'number', required: true },
    { key: 'cleaning_frequency',   label: 'Cleaning frequency preference', type: 'select', options: ['Continuous 24/7', 'Continuous + Day Porter', 'Nightly'] },
    YN('restricted_area_access',   'Restricted-area credentialing available'),
    F.notes,
  ],
};

export default function LiveDemoPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('pick');   // 'pick' | 'form'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fields = useMemo(() => (selected ? TYPE_FIELDS[selected.key] || [] : []), [selected]);

  const pick = (t) => {
    setSelected(t);
    setForm({});
    setError(null);
    setStep('form');
  };

  const validate = () => {
    const missing = fields.filter((f) => f.required && !String(form[f.key] || '').trim());
    if (missing.length) {
      setError(`Please complete: ${missing.map((f) => f.label).join(', ')}`);
      return false;
    }
    setError(null);
    return true;
  };

  const launch = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/live-demo/launch', {
        method: 'POST',
        body: {
          business_type: selected.key,
          submitted: form,
          // Iter-110 · Item 8 — curated amenities auto-applied to the demo board.
          amenities: DEMO_AMENITIES[selected.key] || [],
        },
      });
      const redirect = res?.redirect_url || `/register-property?demo=1&type=${selected.key}`;
      const demoSessionId = res?.demo_session_id;

      try {
        sessionStorage.setItem('necs.live_demo.payload', JSON.stringify({
          business_type: selected.key,
          submitted: form,
          demo_session_id: demoSessionId,
          launched_at: new Date().toISOString(),
        }));
      } catch { /* ignore */ }
      toast.success('Demo sandbox ready — launching your dashboard');
      navigate(redirect);
    } catch (e) {
      setError(e.detail || 'Could not launch demo. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="property-intake-page"
      data-testid="live-demo-page"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: '#5eead4' }} data-testid="live-demo-back">
          <ChevronLeft className="h-4 w-4" /> Back to Home
        </Link>

        <div className="mt-8 text-center">
          <p className="text-[10px] uppercase tracking-[0.4em] mb-2" style={{ color: '#5eead4' }}>
            · NECS Interactive Demo ·
          </p>
          <h1
            className="necs-web-page-title text-white mb-3"
            data-testid="live-demo-heading"
          >
            EXPERIENCE THE PLATFORM — <span className="necs-web-title-accent">NO SIGN-UP</span>
          </h1>
          <p className="text-sm text-zinc-300 max-w-2xl mx-auto">
            Pick your business type and we'll drop you into a Property Manager sandbox
            fully populated with realistic data for that service line. Nothing gets saved.
          </p>
        </div>

        {step === 'pick' && (
          <div className="mt-10">
            <PropertyTypePicker onSelect={pick} testIdPrefix="live-demo" />
          </div>
        )}

        {step === 'form' && selected && (
          <div className="mt-10 max-w-3xl mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <button
                type="button"
                onClick={() => { setStep('pick'); setError(null); }}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full transition"
                style={{ backgroundColor: '#ffffff', color: '#5eead4', border: '1px solid #5eead4' }}
                data-testid="live-demo-change-type"
              >
                ← Change business type
              </button>
              <span
                className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold"
                style={{ backgroundColor: 'rgba(94, 234, 212, 0.15)', color: '#5eead4' }}
                data-testid="live-demo-selected-type"
              >
                {selected.necs}
              </span>
            </div>

            <Card
              className="p-6"
              style={{
                backgroundColor: 'rgba(2, 6, 23, 0.7)',
                border: '1px solid rgba(94, 234, 212, 0.3)',
                backdropFilter: 'blur(20px)',
              }}
              data-testid="live-demo-form-card"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] mb-1 font-bold" style={{ color: '#5eead4' }}>
                Demo Sandbox Setup
              </p>
              <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {selected.label.toUpperCase()}
              </h2>
              <p className="text-xs text-zinc-400 mb-5">
                Every field flows straight into your sandbox so it looks like a real account from first login.
              </p>

              <div className="grid sm:grid-cols-2 gap-3 mb-5" data-testid="live-demo-fields">
                {fields.map((f) => (
                  <FieldRow
                    key={f.key}
                    field={f}
                    value={form[f.key] || ''}
                    onChange={(v) => setForm({ ...form, [f.key]: v })}
                    fullWidth={f.type === 'textarea' || f.type === 'address'}
                  />
                ))}
              </div>

              {/* Iter-110 · Item 8 — Pre-populated, read-only amenities panel.
                  Curated per property type and auto-applied to the demo
                  launch board on submit. Prospects cannot edit it. Property
                  plan upload was removed from ALL demo forms (Item 7) — it
                  remains on real registration forms only. */}
              <div
                className="p-4 rounded-lg mb-5"
                style={{ backgroundColor: 'rgba(94,234,212,0.06)', border: '1px solid rgba(94,234,212,0.35)' }}
                data-testid="live-demo-amenities-panel"
              >
                <p className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: '#5eead4' }}>
                  Included Amenities — Serviced by NECS
                </p>
                <p className="text-[11px] text-zinc-400 mb-3">
                  Pre-configured for a {selected.label.toLowerCase()} and applied automatically to your
                  demo dashboard — a realistic, fully set-up experience from day one. Read-only during the demo.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(DEMO_AMENITIES[selected.key] || []).map((a) => (
                    <div
                      key={a.name}
                      className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg"
                      style={{ backgroundColor: 'rgba(15,23,42,0.9)', border: '1px solid rgba(94,234,212,0.3)' }}
                      data-testid={`live-demo-amenity-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      <span className="text-xs text-white">{a.name}</span>
                      <span
                        className="text-[10px] font-black px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: 'rgba(94,234,212,0.12)', color: '#5eead4', border: '1px solid #5eead4' }}
                      >
                        ×{a.qty}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {error && (
                <div
                  className="mb-3 p-2 rounded-md text-xs"
                  style={{ backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid #ef4444', color: '#fecaca' }}
                  data-testid="live-demo-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="button"
                onClick={launch}
                disabled={submitting}
                className="w-full text-sm font-black uppercase tracking-widest flex items-center justify-center gap-2 py-6"
                style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #6366f1 100%)', color: '#fff' }}
                data-testid="live-demo-launch-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                {submitting ? 'Provisioning demo…' : 'Launch My Demo'}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </Button>
              <p className="text-[10px] text-center text-zinc-400 mt-3">
                No account required. Nothing gets saved.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function FieldRow({ field, value, onChange, fullWidth }) {
  const label = (
    <label className="block text-[10px] uppercase tracking-widest text-zinc-300 mb-1">
      {field.label}{field.required && <span className="text-red-400"> *</span>}
    </label>
  );
  const commonInputStyle = {
    backgroundColor: 'rgba(15,23,42,0.9)', border: '1px solid #334155', color: '#fff',
  };
  let control;
  if (field.type === 'textarea') {
    control = (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-3 py-2 text-sm rounded-md"
        style={commonInputStyle}
        data-testid={`live-demo-field-${field.key}`}
      />
    );
  } else if (field.type === 'select') {
    control = (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm rounded-md"
        style={commonInputStyle}
        data-testid={`live-demo-field-${field.key}`}
      >
        <option value="">Select…</option>
        {field.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  } else {
    control = (
      <Input
        type={field.type === 'address' ? 'text' : field.type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.type === 'address' ? '123 Main St, City, ST 00000' : ''}
        className="text-sm"
        style={commonInputStyle}
        data-testid={`live-demo-field-${field.key}`}
      />
    );
  }
  return (
    <div className={fullWidth ? 'sm:col-span-2' : ''}>
      {label}
      {control}
    </div>
  );
}
